import { useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import RelatorioCaminhaoPDF from '../components/RelatorioCaminhaoPDF';
import { LOGO_EXEMPLO, ASSINATURA_EXEMPLO } from '../components/pdf/exemploImagens';
import { calcularFcj, calcularFcjEsperado, calcularFcmj, classificarFcj } from '../utils/fcjCalculo';

// Página de desenvolvimento: mostra o PDF do "Relatório por caminhão" ao vivo (hot reload),
// sem precisar de backend. Acesse em /app/dev-pdf-preview-caminhao. Não aparece no menu.
// Cobre: caminhão com todos os resultados, caminhão com resultado parcial (linha preta
// "cortada" no gráfico), caminhão sem nenhum resultado (linha preta ausente), caminhão com
// muitos CPs (testa quebra de página dentro de um caminhão), logo/endereço no cabeçalho e o
// toggle cor/sem cor da tabela de resultados (equivalente ao flagRelatorioCor da obra).

const FCK_MOCK = 30;
const PONDERADOR_MOCK = 0.25;

const cabecalhoBaseMock = {
  cliente: 'Construtora Exemplo Ltda',
  obra: 'Edifício Aurora',
  cnpj: '12.345.678/0001-90',
  endereco: 'Av. das Indústrias, 1500 - Distrito Industrial - Contagem/MG',
  cimento: 'CP II-Z-32',
  fck: String(FCK_MOCK),
  tipoConcreto: 'Convencional',
  tipoCorpoProva: 'Cilíndrico 10x20cm',
  concreteira: 'Concreteira Modelo',
};

// Calcula fcj/corFcj a partir da carga de ruptura, igual à normalização real.
function buildLinha({ id, etiqueta, idadeRuptura, dataRuptura, valrCarga, tipoRuptura, prensa }) {
  if (valrCarga == null) {
    return { id, etiqueta, idadeRuptura, dataRuptura, fcj: null, corFcj: null, tipoRuptura: null, prensa: null, temResultado: false };
  }
  const fcj = calcularFcj({ valrCarga, ponderadorTipoCarga: 1, ponderadorTipoCorpoProva: 1 });
  const fcjEsp = calcularFcjEsperado({ fck: FCK_MOCK, idade: idadeRuptura, ponderadorTipoCimento: PONDERADOR_MOCK });
  const fcmj = calcularFcmj({ fck: FCK_MOCK, idade: idadeRuptura, ponderadorTipoCimento: PONDERADOR_MOCK });
  return {
    id,
    etiqueta,
    idadeRuptura,
    dataRuptura,
    fcj,
    corFcj: classificarFcj({ fcj, fcmj, fcjEsperado: fcjEsp }),
    tipoRuptura,
    prensa,
    temResultado: true,
  };
}

const caminhaoBase = {
  placaCaminhao: 'ABC-1234',
  lacre: 'LAC-001',
  valrVolume: 8,
  dataSaidaUsina: '2026-07-10T07:30:00',
  dataInicioDescarga: '2026-07-10T08:10:00',
  dataFimDescarga: '2026-07-10T08:30:00',
  dataConcretagem: '2026-07-10T08:00:00',
};

// Caminhão A: todos os CPs com resultado — valida tabela + 3 curvas completas + badge aprovado.
const grupoCompleto = {
  caminhao: { ...caminhaoBase, id: 'mock-a', placaCaminhao: 'ABC-1234', lacre: 'LAC-001' },
  fck: FCK_MOCK,
  ponderadorTipoCimento: PONDERADOR_MOCK,
  linhas: [
    buildLinha({ id: 'a1', etiqueta: '644', idadeRuptura: 7, dataRuptura: '2026-07-17', valrCarga: 34.17, tipoRuptura: 'Cônica', prensa: 'Prensa 01' }),
    buildLinha({ id: 'a2', etiqueta: '2378', idadeRuptura: 28, dataRuptura: '2026-08-07', valrCarga: 42.51, tipoRuptura: 'Cônica', prensa: 'Prensa 01' }),
    buildLinha({ id: 'a3', etiqueta: '2379', idadeRuptura: 28, dataRuptura: '2026-08-07', valrCarga: 52.98, tipoRuptura: 'Cisalhamento', prensa: 'Prensa 02' }),
  ],
};

// Caminhão B: só 3 e 7 dias com resultado (14 e 28 "aguardando") — valida linha preta cortada.
const grupoParcial = {
  caminhao: { ...caminhaoBase, id: 'mock-b', placaCaminhao: 'DEF-5678', lacre: 'LAC-002' },
  fck: FCK_MOCK,
  ponderadorTipoCimento: PONDERADOR_MOCK,
  linhas: [
    buildLinha({ id: 'b1', etiqueta: '650', idadeRuptura: 3, dataRuptura: '2026-07-13', valrCarga: 18.4, tipoRuptura: 'Cônica', prensa: 'Prensa 02' }),
    buildLinha({ id: 'b2', etiqueta: '651', idadeRuptura: 7, dataRuptura: '2026-07-17', valrCarga: 26.9, tipoRuptura: 'Cônica', prensa: 'Prensa 02' }),
    buildLinha({ id: 'b3', etiqueta: '652', idadeRuptura: 14, dataRuptura: '2026-07-24', valrCarga: null, tipoRuptura: null }),
    buildLinha({ id: 'b4', etiqueta: '653', idadeRuptura: 28, dataRuptura: '2026-08-07', valrCarga: null, tipoRuptura: null }),
  ],
};

// Caminhão C: nenhum CP com resultado — gráfico só com grid/eixos/curvas verde-vermelha.
const grupoSemResultado = {
  caminhao: { ...caminhaoBase, id: 'mock-c', placaCaminhao: 'GHI-9012', lacre: 'LAC-003' },
  fck: FCK_MOCK,
  ponderadorTipoCimento: PONDERADOR_MOCK,
  linhas: [
    buildLinha({ id: 'c1', etiqueta: '660', idadeRuptura: 7, dataRuptura: '2026-07-17', valrCarga: null, tipoRuptura: null }),
    buildLinha({ id: 'c2', etiqueta: '661', idadeRuptura: 28, dataRuptura: '2026-08-07', valrCarga: null, tipoRuptura: null }),
  ],
};

// Caminhão D: muitos CPs — valida quebra de página dentro do caminhão (cabeçalho fixed repete).
const grupoMuitosCps = {
  caminhao: { ...caminhaoBase, id: 'mock-d', placaCaminhao: 'JKL-3456', lacre: 'LAC-004' },
  fck: FCK_MOCK,
  ponderadorTipoCimento: PONDERADOR_MOCK,
  linhas: Array.from({ length: 18 }, (_, i) =>
    buildLinha({
      id: `d${i + 1}`,
      etiqueta: String(700 + i),
      idadeRuptura: 28,
      dataRuptura: '2026-08-07',
      valrCarga: 30 + (i % 5) * 3,
      tipoRuptura: 'Cônica',
      prensa: i % 2 === 0 ? 'Prensa 01' : 'Prensa 02',
    })
  ),
};

const gruposMock = [grupoCompleto, grupoParcial, grupoSemResultado, grupoMuitosCps];

function DevPdfPreviewCaminhaoPage() {
  const [corAtiva, setCorAtiva] = useState(true);
  const [comImagens, setComImagens] = useState(true);

  const personalizacao = comImagens
    ? {
        logoCliente: LOGO_EXEMPLO,
        assinaturas: [{ src: ASSINATURA_EXEMPLO, nome: 'Eng. Fulano de Tal' }, { src: ASSINATURA_EXEMPLO, nome: 'Eng. Beltrano da Silva' }],
      }
    : {};

  return (
    <div style={{ height: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <input type="checkbox" checked={corAtiva} onChange={(e) => setCorAtiva(e.target.checked)} />
        Relatório colorido (flagRelatorioCor da obra)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <input type="checkbox" checked={comImagens} onChange={(e) => setComImagens(e.target.checked)} />
        Com logo/assinaturas de exemplo
      </label>
      <div style={{ flex: 1 }}>
        <PDFViewer key={`${corAtiva}-${comImagens}`} width="100%" height="100%" showToolbar>
          <RelatorioCaminhaoPDF grupos={gruposMock} cabecalhoBase={cabecalhoBaseMock} corAtiva={corAtiva} {...personalizacao} />
        </PDFViewer>
      </div>
    </div>
  );
}

export default DevPdfPreviewCaminhaoPage;
